import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  H3,
  H4,
  IconAnnouncement,
  IconChecked,
  IconCheckmark,
  SecondaryButton,
} from '../../../components';
import classNames from 'classnames';
import { getPaymentMethods } from '../../../util/paymentHelpers';

import css from './SignupHero.module.css';

import BadgeImage from '../../../assets/badge-orange.webp';
import dashboardMock from '../../../assets/vaarmarkt-tablet-business.webp';

import { ReactComponent as Business1Logo } from '../../../assets/businesses/business-1.svg';
import { ReactComponent as Business2Logo } from '../../../assets/businesses/business-2.svg';
import { ReactComponent as Business3Logo } from '../../../assets/businesses/business-3.svg';
import { ReactComponent as Business4Logo } from '../../../assets/businesses/business-4.svg';
import { ReactComponent as Business5Logo } from '../../../assets/businesses/business-5.svg';
import { ReactComponent as Business6Logo } from '../../../assets/businesses/business-6.svg';

const PricingTable = () => {
  const intl = useIntl();

  return (
    <div className={css.pricingTable}>
      <div className={css.pricingContainer}>
        {getPaymentMethods(false, intl).map(plan => (
          <div key={plan.value} className={css.pricingColumn}>
            <h5 className={css.planTitle}>
              {intl.formatMessage({ id: `General.paymentMethods.${plan.value}` })}
            </h5>

            <div className={css.planPrice}>
              {plan.price}
              <div className={css.pricePer}>{plan.pricePer}</div>
            </div>

            <ul className={css.planFeatures}>
              {plan.descriptionList.map(fid => (
                <li key={fid}>
                  <IconChecked />
                  {['max 10', 'to 10', 'onbeperkt', 'Unlimited'].some(str => fid.includes(str)) ? (
                    <strong>
                      {fid} <sup>**</sup>
                    </strong>
                  ) : (
                    fid
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className={css.pricingFooter}>
        <p className={css.pricingFootnote}>
          <FormattedMessage id="SignupHero.business.pricing.footnote.1" />
        </p>
        <p className={css.pricingFootnote}>
          <FormattedMessage id="SignupHero.business.pricing.footnote.2" />
        </p>
      </div>
    </div>
  );
};

const BUSINESS_LOGOS = [
  Business1Logo,
  Business2Logo,
  Business3Logo,
  Business4Logo,
  Business5Logo,
  Business6Logo,
];

const FEATURES = [1, 2, 3, 4];

const scrollToForm = e => {
  e.preventDefault();
  document.getElementById('signup-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const SignupHero = ({ userType }) => {
  if (userType === 'businessSeller') {
    return (
      <div className={css.mock}>
        <div className={css.contentContainer}>
          <div className={css.wrapper}>
            <div className={css.section}>
              <H3>
                <FormattedMessage id="SignupHero.business.title" />
              </H3>
              <div className={css.contentInfo}>
                <H4>
                  <FormattedMessage id="SignupHero.business.subtitle" />
                </H4>
                <div className={css.contentInfoRow}>
                  <p>
                    <FormattedMessage id="SignupHero.business.p1" />
                  </p>
                  <IconAnnouncement />
                </div>
                <p>
                  <FormattedMessage id="SignupHero.business.p2" />
                </p>
              </div>
              <SecondaryButton className={css.cta} onClick={scrollToForm}>
                <FormattedMessage id="SignupHero.business.cta" />
              </SecondaryButton>
            </div>

            <div className={css.section}>
              <H3>
                <FormattedMessage id="SignupHero.business.features.title" />
              </H3>
              <div className={css.featuresGrid}>
                <div className={css.mockupCol}>
                  <img src={dashboardMock} alt="Dashboard mock-up" />
                </div>
                <div className={css.contentInfo}>
                  <ul className={css.featureList}>
                    {FEATURES.map(n => (
                      <li key={n} className={css.featureItem}>
                        <span className={css.featureIcon}>
                          <IconCheckmark />
                        </span>
                        <div>
                          <p className={css.featureTitle}>
                            <FormattedMessage id={`SignupHero.business.features.${n}.title`} />
                          </p>
                          <p className={css.featureBody}>
                            <FormattedMessage id={`SignupHero.business.features.${n}.body`} />
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className={classNames(css.section, css.sectionYellow)}>
              <H3 className={css.pricingTitle}>
                <FormattedMessage id="SignupHero.business.pricing.title" />
              </H3>
              <p>
                <FormattedMessage id="SignupHero.business.pricing.subtitle" />
              </p>
              <PricingTable />
              <SecondaryButton className={css.cta} onClick={scrollToForm}>
                <FormattedMessage id="SignupHero.business.cta" />
              </SecondaryButton>
            </div>

            <div className={classNames(css.section, css.sectionContact)}>
              <div className={css.fullWidthSection}>
                <div className={css.contentInfo}>
                  <H4 className={css.smaller}>
                    <FormattedMessage id="SignupHero.business.contact.title" />
                  </H4>
                  <div className={css.logos}>
                    {BUSINESS_LOGOS.map((Logo, i) => (
                      <Logo key={i} className={css.businessLogo} aria-label={`Business ${i + 1}`} />
                    ))}
                  </div>
                </div>

                <div className={css.contentInfo}>
                  <H4>
                    <FormattedMessage id="SignupHero.business.about.title" />
                  </H4>
                  <p>
                    <FormattedMessage id="SignupHero.business.about.p1" />
                  </p>
                  <p>
                    <FormattedMessage id="SignupHero.business.about.p2" />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className={css.root}>
      <H3 className={css.title}>
        <FormattedMessage id="SignupHero.title" />
      </H3>
      <H4 className={css.subtitle}>
        <FormattedMessage id="SignupHero.subtitle" />
      </H4>

      <ul className={css.list}>
        {[1, 2, 3].map(i => (
          <li key={i}>
            <IconChecked />
            <FormattedMessage id={`SignupHero.item${i}`} />
          </li>
        ))}
      </ul>

      <p className={css.tagline}>
        <FormattedMessage id="SignupHero.tagline" />
      </p>

      <SecondaryButton className={css.cta} onClick={scrollToForm}>
        <FormattedMessage id="SignupHero.business.cta" />
      </SecondaryButton>

      <img src={BadgeImage} className={css.badge} />
    </section>
  );
};

export default SignupHero;
